import React, { useEffect, useState } from 'react';
import { FileText, Download, Upload, X, User, MapPin, CreditCard, Phone, Mail, Save, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface RelativePhone {
  numero: string;
  relacao: string;
}

interface Contract {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  bank: string;
  relative_phones: RelativePhone[] | null;
  loan_amount: number;
  total_amount: number;
  status: string;
  approval_status: string;
  rejection_reason: string;
  vendor_rejection_reason?: string;
  observations?: string | null;
  created_at: string;
  resubmitted_at: string | null;
  seller: {
    email: string;
  } | null;
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
}

interface EditableFields {
  client_name: string;
  cpf: string;
  phone: string;
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  bank: string;
  relative_phones: RelativePhone[];
}

const formatCPF = (cpf: string) => {
  const n = (cpf || '').replace(/\D/g, '');
  return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export default function RejectedContracts() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [editFields, setEditFields] = useState<EditableFields | null>(null);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newObservation, setNewObservation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchRejectedContracts();
  }, [user]);

  const fetchRejectedContracts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('contracts')
        .select(`
          *, seller:seller_id(email), contract_files(*)
        `)
        .in('approval_status', ['rejected', 'vendor_rejected'])
        .eq('seller_id', user?.id)
        .eq('unit_id', user?.unit_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContracts(data || []);
    } catch (err) {
      console.error('Erro ao carregar contratos recusados:', err);
      setError('Erro ao carregar contratos recusados');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (contract: Contract) => {
    setSelectedContract(contract);
    setEditFields({
      client_name: contract.client_name || '',
      cpf: contract.cpf || '',
      phone: contract.phone || '',
      email: contract.email || '',
      address: contract.address || '',
      business_address: contract.business_address || '',
      pix_key: contract.pix_key || '',
      bank: contract.bank || '',
      relative_phones: Array.isArray(contract.relative_phones) ? contract.relative_phones : [],
    });
    setNewFiles([]);
    setNewObservation('');
  };

  const closeEdit = () => {
    setSelectedContract(null);
    setEditFields(null);
    setNewFiles([]);
    setNewObservation('');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileTypeLabel = (file: File) => {
    if (file.type.startsWith('image/')) return 'Imagem';
    if (file.type === 'application/pdf') return 'PDF';
    return 'Documento';
  };

  const updateRelativePhone = (index: number, field: keyof RelativePhone, value: string) => {
    if (!editFields) return;
    const updated = [...editFields.relative_phones];
    updated[index] = { ...updated[index], [field]: value };
    setEditFields({ ...editFields, relative_phones: updated });
  };

  const addRelativePhone = () => {
    if (!editFields) return;
    setEditFields({
      ...editFields,
      relative_phones: [...editFields.relative_phones, { numero: '', relacao: '' }],
    });
  };

  const removeRelativePhone = (index: number) => {
    if (!editFields) return;
    setEditFields({
      ...editFields,
      relative_phones: editFields.relative_phones.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    if (!selectedContract || !editFields) return;
    if (!editFields.client_name.trim() || !editFields.cpf.trim()) {
      setError('Nome e CPF sao obrigatorios.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const cleanRelativePhones = editFields.relative_phones.filter(
        p => (p.numero || '').trim() !== '' || (p.relacao || '').trim() !== ''
      );

      const appendedReason = newObservation.trim()
        ? `${selectedContract.rejection_reason || ''}\n\nObservacao (${new Date().toLocaleDateString('pt-BR')}): ${newObservation.trim()}`
        : selectedContract.rejection_reason;

      const { error: updateError } = await supabase
        .from('contracts')
        .update({
          client_name: editFields.client_name.trim(),
          cpf: editFields.cpf.replace(/\D/g, ''),
          phone: editFields.phone,
          email: editFields.email,
          address: editFields.address,
          business_address: editFields.business_address,
          pix_key: editFields.pix_key,
          bank: editFields.bank,
          relative_phones: cleanRelativePhones,
          approval_status: 'pending',
          resubmitted_at: new Date().toISOString(),
          rejection_reason: appendedReason,
        })
        .eq('id', selectedContract.id);

      if (updateError) throw updateError;

      for (const file of newFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${selectedContract.id}/${fileName}`;

        const { data: signedUrlData, error: signedUrlError } = await supabase
          .storage
          .from('contract-files')
          .createSignedUploadUrl(filePath);

        if (signedUrlError || !signedUrlData?.signedUrl) {
          console.error('Erro ao gerar signed URL:', signedUrlError);
          throw signedUrlError || new Error('Falha ao gerar URL de upload');
        }

        const uploadResp = await fetch(signedUrlData.signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!uploadResp.ok) {
          throw new Error(`Falha ao enviar ${file.name}`);
        }

        const { data: { publicUrl } } = supabase
          .storage
          .from('contract-files')
          .getPublicUrl(filePath);

        await supabase.from('contract_files').insert([{
          contract_id: selectedContract.id,
          unit_id: user?.unit_id,
          file_name: file.name,
          file_type: file.type,
          file_url: publicUrl,
        }]);
      }

      await fetchRejectedContracts();
      setSuccessMessage(`Cadastro de ${editFields.client_name} atualizado e reenviado para aprovacao.`);
      setTimeout(() => setSuccessMessage(null), 4000);
      closeEdit();
    } catch (err) {
      console.error('Erro ao atualizar contrato:', err);
      const message = err instanceof Error ? err.message : 'Erro ao atualizar contrato';
      setError(`Erro ao atualizar contrato: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Contratos Recusados</h1>
        <p className="text-sm text-gray-500 mt-1">
          Corrija as informacoes cadastradas e reenvie para aprovacao.
        </p>
      </div>

      {successMessage && (
        <div className="flex items-start gap-3 p-4 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-800">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-2xl border border-red-200 bg-red-50 text-red-800">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {contracts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">
          Nenhum contrato recusado encontrado.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {contracts.map((contract) => (
            <div key={contract.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4 gap-3 flex-wrap">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">{contract.client_name}</h2>
                  <p className="text-sm text-gray-600 truncate">{contract.email}</p>
                  <p className="text-sm text-gray-600">
                    Valor: {formatCurrency(contract.loan_amount)}
                  </p>
                  <span className={`inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${contract.approval_status === 'vendor_rejected' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
                    {contract.approval_status === 'vendor_rejected' ? 'Recusado pelo vendedor' : 'Recusado pelo gestor'}
                  </span>
                </div>
                {contract.approval_status === 'rejected' && (
                  <button
                    onClick={() => openEdit(contract)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium flex-shrink-0"
                  >
                    Editar e reenviar
                  </button>
                )}
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Motivo da Recusa:</h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {contract.approval_status === 'vendor_rejected'
                    ? (contract.vendor_rejection_reason || '—')
                    : (contract.rejection_reason || '—')}
                </p>
              </div>

              {contract.contract_files.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Documentos:</h3>
                  <div className="space-y-2">
                    {contract.contract_files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          <span className="text-sm text-gray-900 truncate">{file.file_name}</span>
                        </div>
                        <a
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                        >
                          <Download className="w-5 h-5" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {contract.resubmitted_at && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-600">
                    Reenviado em: {new Date(contract.resubmitted_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedContract && editFields && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-3xl w-full max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-800 truncate">
                  Editar cadastro - {selectedContract.client_name}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Corrija as informacoes e reenvie para aprovacao do gestor.
                </p>
              </div>
              <button
                onClick={closeEdit}
                disabled={isSubmitting}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0 ml-4"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-6">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Motivo da recusa</p>
                  <p className="text-sm text-red-700 whitespace-pre-line mt-1">
                    {selectedContract.rejection_reason || '—'}
                  </p>
                </div>
              </div>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    Dados do Cliente
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nome completo</label>
                    <input
                      type="text"
                      value={editFields.client_name}
                      onChange={e => setEditFields({ ...editFields, client_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">CPF</label>
                    <input
                      type="text"
                      value={editFields.cpf}
                      onChange={e => setEditFields({ ...editFields, cpf: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Telefone
                    </label>
                    <input
                      type="text"
                      value={editFields.phone}
                      onChange={e => setEditFields({ ...editFields, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> E-mail
                    </label>
                    <input
                      type="email"
                      value={editFields.email}
                      onChange={e => setEditFields({ ...editFields, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    Enderecos
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Endereco residencial</label>
                    <textarea
                      value={editFields.address}
                      onChange={e => setEditFields({ ...editFields, address: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Endereco comercial</label>
                    <textarea
                      value={editFields.business_address}
                      onChange={e => setEditFields({ ...editFields, business_address: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    Dados Bancarios
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Chave PIX</label>
                    <input
                      type="text"
                      value={editFields.pix_key}
                      onChange={e => setEditFields({ ...editFields, pix_key: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Banco</label>
                    <input
                      type="text"
                      value={editFields.bank}
                      onChange={e => setEditFields({ ...editFields, bank: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                      Contatos de Emergencia
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={addRelativePhone}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Adicionar
                  </button>
                </div>
                {editFields.relative_phones.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Nenhum contato cadastrado.</p>
                ) : (
                  <div className="space-y-2">
                    {editFields.relative_phones.map((rp, i) => (
                      <div key={i} className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Relacao</label>
                          <input
                            type="text"
                            value={rp.relacao}
                            onChange={e => updateRelativePhone(i, 'relacao', e.target.value)}
                            placeholder="Ex: Mae, Pai, Conjuge..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
                          <input
                            type="text"
                            value={rp.numero}
                            onChange={e => updateRelativePhone(i, 'numero', e.target.value)}
                            placeholder="(00) 00000-0000"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeRelativePhone(i)}
                          className="h-10 w-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    Novos Documentos <span className="text-gray-400 normal-case font-normal">(opcional)</span>
                  </h3>
                </div>
                <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-10 w-10 text-gray-400" />
                    <div className="flex text-sm text-gray-600 justify-center">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                      >
                        <span>Faca upload dos arquivos</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          multiple
                          onChange={handleFileUpload}
                        />
                      </label>
                      <p className="pl-1">ou arraste e solte</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, PDF ate 10MB</p>
                  </div>
                </div>

                {newFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {newFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">
                            {file.name} ({getFileTypeLabel(file)})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <label className="block text-sm font-semibold text-gray-800 uppercase tracking-wide mb-2">
                  Observacao <span className="text-gray-400 normal-case font-normal">(opcional)</span>
                </label>
                <textarea
                  value={newObservation}
                  onChange={e => setNewObservation(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Descreva as correcoes realizadas..."
                />
              </section>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEdit}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvar e reenviar para aprovacao
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
